import { useEffect, useState, useMemo } from 'react'
import { useDiscountCode } from '../../contexts/DiscountContext'
import { CookieKeys, getCookie } from '../../services/Cookies'
import { Storage, StorageKeys } from '../../services/Storage'
import { SessionKeys, SessionStorage } from '../../services/Session'
import { CloseButton, DiscountBannerRoot } from './DiscountBanner.styles'
import { DefaultBanner } from './DiscountBannerTypes/Default/DefaultBanner'
import { BlackFridayBanner } from './DiscountBannerTypes/BlackFriday/BlackFridayBanner'
import { PromotionalBanner } from './DiscountBannerTypes/Promotion/PromotionalBanner'
import { TreatsForSixMonthsBanner } from './DiscountBannerTypes/TreatsForSixMonths/TreatsForSixMonths'
import { FourthJulyBanner } from './DiscountBannerTypes/FourthJuly/FourthJulyBanner'
import { NationalDogDayBanner } from './DiscountBannerTypes/NationalDogDay/NationalDogDayBanner'
import { BarkerBedFourtyBanner } from './DiscountBannerTypes/BarkerBedForty/BarkerBedForty'
import { BLACK_FRIDAY_DISCOUNT_CODE } from '../../models/Discount'

// Banner type definitions
type BannerType =
  | 'blackFriday'
  | 'fourthJuly'
  | 'nationalDogDay'
  | 'promotion'
  | 'gutHealth'
  | 'default'
  | 'treatsForSixMonths'
  | 'barkerBedForty'
// Discount code configuration
const DISCOUNT_CODES = {
  blackFriday: [
    BLACK_FRIDAY_DISCOUNT_CODE,
    'DOGBF100',
    'DOGSGET100',
    'BESTDEAL100',
    'BIGDEAL100',
    'DOGTHANKS100',
    'GOODDOG100',
  ],
  fourthJuly: ['FIREWORK50'],
  nationalDogDay: ['HAPPYDOG40'],
  percentPlusItem: ['GUTHEALTH20', 'GUTLAB25', 'GUTCHECK25', 'HAPPYGUT30', 'DFA40GUT'],
  treatsForOneYear: ['TREATS730', 'FUSARO801'],
  treatsForSixMonths: ['REALTREATS40'],
  barkerBedForty: ['HAPPYSLEEP40', 'HEALTHYSLEEP40', 'GOODSLEEP40', 'DFASLEEP50'],
} as const

// Utility function to determine banner type
const getBannerType = (code: string): BannerType => {
  if (DISCOUNT_CODES.blackFriday.includes(code as any)) return 'blackFriday'
  if (DISCOUNT_CODES.fourthJuly.includes(code as any)) return 'fourthJuly'
  if (DISCOUNT_CODES.nationalDogDay.includes(code as any)) return 'nationalDogDay'
  if (DISCOUNT_CODES.percentPlusItem.includes(code as any)) return 'promotion'
  if (DISCOUNT_CODES.treatsForSixMonths.includes(code as any)) return 'treatsForSixMonths'
  if (DISCOUNT_CODES.barkerBedForty.includes(code as any)) return 'barkerBedForty'
  return 'default'
}

type DiscountBannerProps = {
  pathName: string
}

export const DiscountBanner = ({ pathName }: DiscountBannerProps) => {
  const [isHidden, setIsHidden] = useState(false)
  const [isLoggedIn, setIsLoggedIn] = useState(true)
  const [displayText, setDisplayText] = useState('')
  const [displayAmount, setDisplayAmount] = useState('')
  const [displayFreeCopy, setDisplayFreeCopy] = useState('')
  const {
    discountCodeData,
    defaultDiscountCode,
    isLoading,
    refetchDiscountCode: refetch,
  } = useDiscountCode()

  const storedDiscountCode = Storage.get(StorageKeys.PROMO_CODE) ?? ''

  //pages banner will display
  const allowedPages =
    pathName.length === 1 ||
    [
      '/home',
      '/meal-plan/step-1',
      '/meal-plan/step-2',
      '/meal-plan/step-3',
      '/meal-plan/step-4',
      '/meal-plan/checkout',
      '/why-we-feed-raw',
      '/our-story',
      '/products',
      '/recipes',
      '/recipes/beef',
      '/recipes/lamb',
      '/recipes/chicken',
      '/recipes/turkey',
      '/recipes/duck',
      '/recipes/venison',
      '/recipes/freeze-dried-raw-beef',
      '/recipes/freeze-dried-raw-chicken',
      '/raw-food-subscription',
      '/raw-benefits',
    ].some((page) => pathName.includes(page))

  //pages that are not allowed to click on the banner which will link to the meal plan
  const disableClickPages = [
    '/meal-plan/step-1',
    '/meal-plan/step-2',
    '/meal-plan/step-3',
    '/meal-plan/step-4',
    '/meal-plan/checkout',
  ].some((page) => pathName.includes(page))

  useEffect(() => {
    if (Boolean(SessionStorage().getItem(SessionKeys.BANNER_HIDE))) {
      setIsHidden(true)
    }
    setIsLoggedIn(Boolean(getCookie(CookieKeys.SESSION)))
    if (!discountCodeData) return

    if (!discountCodeData.isActive) {
      // check if the discount code is active
      Storage.set(StorageKeys.PROMO_CODE, defaultDiscountCode)
      refetch()
      return
    }

    setDisplayText(discountCodeData.displayText)
    setDisplayAmount(discountCodeData.displayAmount)
    setDisplayFreeCopy(discountCodeData.displayFreeCopy)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [discountCodeData, refetch])

  useEffect(() => {
    if (!isHidden) return
    SessionStorage().setItem(SessionKeys.BANNER_HIDE, 'true')
  }, [isHidden])

  // Determine which banner type to show (before early return)
  const discountType = useMemo(() => getBannerType(storedDiscountCode), [storedDiscountCode])

  if (!displayAmount || isLoading || !allowedPages || isLoggedIn) return null

  // Render the appropriate banner component
  const renderBannerContent = () => {
    switch (discountType) {
      case 'blackFriday':
        return (
          <BlackFridayBanner disableClickPages={disableClickPages} displayAmount={displayAmount} />
        )
      case 'fourthJuly':
        return (
          <FourthJulyBanner disableClickPages={disableClickPages} displayAmount={displayAmount} />
        )
      case 'nationalDogDay':
        return (
          <NationalDogDayBanner
            disableClickPages={disableClickPages}
            displayAmount={displayAmount}
          />
        )
      case 'promotion':
        return (
          <PromotionalBanner disableClickPages={disableClickPages} displayAmount={displayAmount} />
        )
      case 'treatsForSixMonths':
        return (
          <TreatsForSixMonthsBanner
            disableClickPages={disableClickPages}
            displayAmount={displayAmount}
          />
        )
      case 'barkerBedForty':
        return (
          <BarkerBedFourtyBanner
            disableClickPages={disableClickPages}
            displayAmount={displayAmount}
          />
        )
      default:
        return (
          <DefaultBanner
            code={storedDiscountCode}
            displayText={displayText}
            displayAmount={displayAmount}
            displayFreeCopy={displayFreeCopy}
            disableClickPages={disableClickPages}
            treatsForOneYear={[...DISCOUNT_CODES.treatsForOneYear]}
          />
        )
    }
  }

  return (
    <DiscountBannerRoot type={discountType} display={!isHidden}>
      {renderBannerContent()}
      <CloseButton hoverColor="red" aria-label="Close" onClick={() => setIsHidden(true)}>
        x
      </CloseButton>
    </DiscountBannerRoot>
  )
}

DiscountBanner.displayName = 'DiscountBanner'
